import { useCallback, useEffect, useRef, useState } from 'react';

import {
  UserCommunication,
  UserCommunicationContentDetails,
} from '@this+that/models';
import axios from 'axios';
import log from 'loglevel';

import { useGeneratePresignedUrlForUserContent } from '../../../services/userCommunications.service';
import { useUserCommunicationsStore } from '../../../store';
import { isDraft } from '../../../utils/userCommunicationHelpers';
import { useActiveInboxEntriesSlice } from '../../InboxEntries';

const LOG_MESSAGES = {
  DISCARD_STALE_CONTENT: 'Discarding stale content fetch: draft saved since',
} as const;

const isContentDetailsCacheComplete = (
  userCommunication?: UserCommunication | null,
): boolean => {
  if (!userCommunication?.messageRawText) {
    return false;
  }
  if (isDraft(userCommunication)) {
    return true;
  }
  return (userCommunication.attachments ?? []).every(
    (attachment) => !attachment.fileName || Boolean(attachment.content),
  );
};

export const useFetchUserCommunicationContentDetails = (
  userCommunication?: UserCommunication | null,
  shouldFetch: boolean = true,
) => {
  const { s3Key, attachments, messageRawText, messageText } =
    userCommunication ?? {};

  const { updateUserCommunicationContentFields } = useUserCommunicationsStore();
  const { modifyInboxEntryUserCommunication } = useActiveInboxEntriesSlice();

  const [userCommunicationContentDetails, setUserCommunicationContentDetails] =
    useState<UserCommunicationContentDetails | null>(
      userCommunication
        ? {
            attachments: attachments ?? [],
            messageRawText: messageRawText ?? '',
            messageText: messageText ?? '',
          }
        : null,
    );

  const hasContentDetailsInCache =
    isContentDetailsCacheComplete(userCommunication);

  // FIX #9: Was setting state (setPrevMessageId + setUserCommunicationContentDetails) during the
  // render phase via an if-block. Moved into useEffect to avoid state mutations during render.
  useEffect(() => {
    setUserCommunicationContentDetails(
      userCommunication
        ? {
            attachments: userCommunication.attachments ?? [],
            messageRawText: userCommunication.messageRawText ?? '',
            messageText: userCommunication.messageText ?? '',
          }
        : null,
    );
  }, [userCommunication?.messageId]);

  const [isLoading, setIsLoading] = useState(
    shouldFetch && !hasContentDetailsInCache,
  );
  const currentMessageId = userCommunication?.messageId;
  const [errorTarget, setErrorTarget] = useState<{
    messageId?: string;
  } | null>(null);
  const hasError =
    errorTarget !== null && errorTarget.messageId === currentMessageId;

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [userCommunication?.messageId]);

  const { generatePresignedUrlForUserContent } =
    useGeneratePresignedUrlForUserContent();

  const handleFetchUserCommunicationContentDetails =
    useCallback(async (): Promise<UserCommunicationContentDetails | null> => {
      setErrorTarget(null);
      try {
        setIsLoading(true);
        if (!s3Key) {
          return null;
        }
        const presignedUrl = await generatePresignedUrlForUserContent(s3Key);
        if (!presignedUrl) {
          setErrorTarget({ messageId: currentMessageId });
          return null;
        }

        const { data } =
          await axios.get<UserCommunicationContentDetails>(presignedUrl);

        return data;
      } catch (error) {
        log.error('Error fetching user communications:', error);
        setErrorTarget({ messageId: currentMessageId });

        return null;
      }
    }, [generatePresignedUrlForUserContent, s3Key, currentMessageId]);

  const fetchData = async () => {
    const fetchStartedAt = Date.now();
    const targetMessageId = userCommunication?.messageId;
    const targetDraftId = userCommunication?.draftId;
    const targetInboxEntryIds = userCommunication?.inboxEntryIds;

    const data = await handleFetchUserCommunicationContentDetails();
    if (!data) {
      setIsLoading(false);
      return;
    }

    setUserCommunicationContentDetails(data);
    setIsLoading(false);

    if (!targetMessageId || !data.messageRawText) {
      return;
    }

    // A save (autosave or send-echo) confirmed for this row's draft after
    // this fetch started means the S3 content we just loaded is stale —
    // applying it would clobber a fresher recipients/body edit already in
    // the store. Discard the whole update rather than merging it in.
    const { unconfirmedDrafts, deletedDrafts } =
      useUserCommunicationsStore.getState();
    const hasNewerConfirmedSave =
      !!targetDraftId &&
      (unconfirmedDrafts[targetDraftId] ?? 0) >= fetchStartedAt;
    const wasDiscarded = !!targetDraftId && targetDraftId in deletedDrafts;

    if (hasNewerConfirmedSave || wasDiscarded) {
      log.debug(LOG_MESSAGES.DISCARD_STALE_CONTENT, { targetMessageId });
      return;
    }

    const contentFields = {
      messageRawText: data.messageRawText,
      messageText: data.messageText,
      attachments: data.attachments,
    };

    targetInboxEntryIds?.forEach((entryId) => {
      modifyInboxEntryUserCommunication(entryId, {
        messageId: targetMessageId,
        draftId: targetDraftId,
        ...contentFields,
      });
    });

    updateUserCommunicationContentFields(targetMessageId, contentFields);
  };

  useEffect(() => {
    if (shouldFetch && !hasContentDetailsInCache && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
  }, [shouldFetch, hasContentDetailsInCache, userCommunication?.messageId]);

  return {
    userCommunicationContentDetails,
    isLoading,
    hasError,
  };
};
